import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';

interface InvoicePendingCardProps {
  invoiceNumber?: string;
  invoiceUrl?: string;
  pdfUrl?: string;
  amountCents: number;
  status: 'open' | 'paid' | 'void' | 'uncollectible' | 'payment_failed' | 'draft';
  dueDate?: string;
  createdAt?: string;
  paymentContext?: 'initial_payment' | 'change_order';
}

export function InvoicePendingCard({
  invoiceNumber,
  invoiceUrl,
  pdfUrl,
  amountCents,
  status,
  dueDate,
  createdAt,
  paymentContext = 'initial_payment',
}: InvoicePendingCardProps) {
  const { paymentProcessingEnabled, isLoading: flagLoading } = usePaymentProcessingEnabled();

  if (flagLoading || !paymentProcessingEnabled) return null;

  const formattedAmount = (amountCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const getStatusBadge = () => {
    switch (status) {
      case 'open':
        return (
          <Badge variant="outline" className="text-amber-600 border-amber-600">
            <Clock className="h-3 w-3 mr-1" />
            Awaiting Payment
          </Badge>
        );
      case 'paid':
        return (
          <Badge variant="outline" className="text-green-600 border-green-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case 'void':
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Voided
          </Badge>
        );
      case 'uncollectible':
      case 'payment_failed':
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            Payment Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            {status}
          </Badge>
        );
    }
  };

  const getContextLabel = () => {
    return paymentContext === 'change_order' ? 'Change Order Invoice' : 'Purchase Order Invoice';
  };

  return (
    <Card className="border-purple-200 bg-purple-50/50 dark:border-purple-900 dark:bg-purple-950/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-lg">{getContextLabel()}</CardTitle>
          </div>
          {getStatusBadge()}
        </div>
        {invoiceNumber && (
          <CardDescription>Invoice #{invoiceNumber}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Amount Due</span>
          <span className="text-xl font-semibold">{formattedAmount}</span>
        </div>

        {dueDate && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Due Date</span>
            <span>Due on Receipt</span>
          </div>
        )}

        {createdAt && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Sent</span>
            <span>{formatDistanceToNow(new Date(createdAt), { addSuffix: true })}</span>
          </div>
        )}

        {status === 'open' && (
          <div className="pt-2 space-y-2">
            <p className="text-sm text-muted-foreground">
              An invoice has been sent to your accounts payable email. Once payment is received, the {paymentContext === 'change_order' ? 'adjustment' : 'purchase order'} will be processed automatically.
            </p>
            
            {invoiceUrl && (
              <Button
                variant="default"
                size="sm"
                className="w-full"
                onClick={() => window.open(invoiceUrl, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Pay or Download Invoice
              </Button>
            )}
          </div>
        )}

        {status === 'paid' && (
          <div className="pt-2">
            <p className="text-sm text-green-600">
              Payment received. Your {paymentContext === 'change_order' ? 'change order' : 'purchase order'} is being processed.
            </p>
          </div>
        )}

        {(status === 'payment_failed' || status === 'uncollectible') && (
          <div className="pt-2 space-y-2">
            <p className="text-sm text-destructive">
              There was an issue processing your payment. Please try again or contact support.
            </p>
            {invoiceUrl && (
              <Button
                variant="default"
                size="sm"
                onClick={() => window.open(invoiceUrl, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Retry Payment
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
